/**
 * Corporate (B2B) Commerce — shared types. Client-safe: no backend access.
 *
 * Corporate packs reuse the existing Pack Builder Engine. Everything here is
 * additive; Discovery and Gift packs never read these types.
 */

export type CorporateQuoteStatus =
  | "requested"
  | "in_review"
  | "quoted"
  | "approved"
  | "rejected"
  | "converted";

export const CORPORATE_QUOTE_STATUSES: CorporateQuoteStatus[] = [
  "requested",
  "in_review",
  "quoted",
  "approved",
  "rejected",
  "converted",
];

export const QUOTE_STATUS_LABEL: Record<CorporateQuoteStatus, string> = {
  requested: "Requested",
  in_review: "In Review",
  quoted: "Quoted",
  approved: "Approved",
  rejected: "Rejected",
  converted: "Converted",
};

/** Staff-driven transitions. Customers only move quoted → approved/rejected. */
export const QUOTE_TRANSITIONS: Record<CorporateQuoteStatus, CorporateQuoteStatus[]> = {
  requested: ["in_review", "rejected"],
  in_review: ["quoted", "rejected"],
  quoted: ["approved", "rejected", "in_review"],
  approved: ["converted", "rejected"],
  rejected: [],
  converted: [],
};

export function canTransition(from: CorporateQuoteStatus, to: CorporateQuoteStatus): boolean {
  return (QUOTE_TRANSITIONS[from] ?? []).includes(to);
}

export type CorporateAddress = {
  fullName: string;
  line1: string;
  line2?: string;
  city: string;
  state?: string;
  postalCode: string;
  country: string;
  phone: string;
};

export const EMPTY_CORPORATE_ADDRESS: CorporateAddress = {
  fullName: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  postalCode: "",
  country: "India",
  phone: "",
};

export type CorporateContact = {
  name: string;
  email: string;
  phone?: string;
};

/** One delivery destination for a corporate order (multi-address shipping). */
export type CorporateShippingTarget = {
  label: string;
  recipientName: string;
  recipientPhone?: string;
  address: CorporateAddress;
  /** Pack slug -> quantity delivered to this address. */
  items: Record<string, number>;
  brandingNotes?: string;
};

/** Per-pack corporate configuration (`corporate_options`). */
export type CorporateOptions = {
  id: string;
  packId: string;
  minOrderQty: number;
  maxOrderQty: number | null;
  requiresGst: boolean;
  allowBranding: boolean;
  brandingNote: string | null;
  leadTimeDays: number;
  quoteValidityDays: number;
  salesContactEmail: string | null;
  salesContactPhone: string | null;
};

export function defaultCorporateOptions(packId = ""): CorporateOptions {
  return {
    id: "",
    packId,
    minOrderQty: 1,
    maxOrderQty: null,
    requiresGst: true,
    allowBranding: false,
    brandingNote: null,
    leadTimeDays: 7,
    quoteValidityDays: 15,
    salesContactEmail: null,
    salesContactPhone: null,
  };
}

/** A bulk pricing tier (`pack_price_tiers`). Prices are stored in paise. */
export type PackPriceTier = {
  id: string;
  packId: string;
  minQty: number;
  maxQty: number | null;
  /** Absolute unit price in paise. Takes precedence over `discountPercent`. */
  unitPriceCents: number | null;
  discountPercent: number | null;
  label: string | null;
  sortOrder: number;
  isActive: boolean;
};

/** JSON-safe value (server functions must return serializable shapes). */
export type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

export type CorporateQuoteItem = {
  id: string;
  quoteId: string;
  packId: string | null;
  variantId: string | null;
  packSlug: string;
  packName: string;
  variantLabel: string;
  qty: number;
  unitPriceCents: number;
  lineTotalCents: number;
  tierLabel: string | null;
  composition: Record<string, JsonValue>;
  notes: string | null;
  sortOrder: number;
};

export type CorporateQuoteEvent = {
  id: string;
  quoteId: string;
  type: string;
  message: string | null;
  actorEmail: string | null;
  createdAt: string;
};

export type CorporateQuote = {
  id: string;
  quoteNumber: string;
  userId: string | null;
  status: CorporateQuoteStatus;
  companyName: string;
  gstNumber: string | null;
  poNumber: string | null;
  contactName: string;
  contactEmail: string;
  contactPhone: string | null;
  billingContact: CorporateContact | null;
  shippingContact: CorporateContact | null;
  billingAddress: CorporateAddress | null;
  shippingAddresses: CorporateShippingTarget[];
  message: string | null;
  internalNotes: string | null;
  salesRepId: string | null;
  salesRepEmail: string | null;
  currency: string;
  subtotalCents: number;
  discountCents: number;
  shippingCents: number;
  taxCents: number;
  totalCents: number;
  validUntil: string | null;
  quotedAt: string | null;
  decidedAt: string | null;
  convertedOrderId: string | null;
  convertedAt: string | null;
  createdAt: string;
  updatedAt: string;
  items: CorporateQuoteItem[];
  events: CorporateQuoteEvent[];
};

/** What the storefront submits when requesting a quote. */
export type QuoteRequestInput = {
  packSlug: string;
  variantId?: string | null;
  qty: number;
  companyName: string;
  gstNumber?: string;
  poNumber?: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  message?: string;
  billingAddress?: CorporateAddress | null;
  shippingAddresses?: CorporateShippingTarget[];
};

/** Corporate details captured at checkout for a direct (non-quote) order. */
export type CorporateCheckoutDetails = {
  companyName: string;
  gstNumber?: string;
  poNumber?: string;
  billingContact?: CorporateContact;
  shippingContact?: CorporateContact;
  shippingAddresses?: CorporateShippingTarget[];
};

/** GSTIN: 2 digit state code, 10 char PAN, entity code, Z, checksum. */
export const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

export function isValidGst(value: string): boolean {
  return GST_REGEX.test(value.trim().toUpperCase());
}

export type CorporateDashboard = {
  revenueCents: number;
  pipelineCents: number;
  averageOrderValueCents: number;
  conversionRate: number;
  counts: Record<CorporateQuoteStatus, number>;
  totalQuotes: number;
  topCustomers: { companyName: string; quotes: number; valueCents: number }[];
  topPacks: { packName: string; qty: number; valueCents: number }[];
};
